import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RedirectCommand, Router, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { AccountStateService } from './account-state.service';
import { AuthService } from './auth.service';
import { LoadingService } from './loading.service';
import { PermissionService } from './permission.service';
import { PosthogService } from './posthog.service';

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  private authService = inject(AuthService);
  private router = inject(Router);
  private permissionService = inject(PermissionService);
  private _accountStateService = inject(AccountStateService);
  private _posthogService = inject(PosthogService);
  private _loadingService = inject(LoadingService);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const url: string = state.url;
    const token = this.getToken(route);
    if (token) {
      if (!this.authService.connecting) this._loadingService.initApp(token);
      return true;
    } else {
      this.authService.noRedirect();
      if (url && !url.includes('supervisor') && url !== '/login') {
        this.authService.redirectUrl = url;
      }
      if (!this.authService.connecting) {
        this._posthogService.captureEvent('redirect /login', { source: 'service', from: 'auth-guard' });
        const loginPath = this.router.parseUrl('/login');
        return new RedirectCommand(loginPath);
      }
    }
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> {
    return !this.isForceChangePassword() && this.permissionService.checkPathPermission(state.url);
  }

  isForceChangePassword(): boolean | undefined {
    return (
      this._accountStateService?.$account()?.password_force_change ||
      this._accountStateService?.$account()?.edit_password
    );
  }

  checkLogin(url: string): boolean {
    if (this.authService.isLoggedIn || '/' + this.authService.getLoginState() === url) {
      return true;
    } else if (!url.includes('supervisor')) {
      this.authService.redirectUrl = url; // Store the attempted URL for redirecting
      return false;
    }
  }

  getToken(route: ActivatedRouteSnapshot): string {
    let token: string = null;
    if (route.queryParams.token) {
      // Don't delete : it's for connect with user on Traceur
      token = route.queryParams.token;
    } else {
      if (localStorage) {
        const storedToken = this.authService.getStoredToken();
        if (storedToken) {
          token = storedToken;
        }
      }
    }
    return token;
  }
}
